import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import CheckRow from 'components/ui/checkRow'
import BiPreviewModal from './components/biPreviewModal'
import * as api from './api.client'
import { AdminUser } from './types'
import { AppMessage } from '../../utilities/messages'
import { hasBiUrl } from 'features/doctors/types'

const emptyUser: AdminUser = {
  id: 0,
  name: '',
  email: '',
  role: '',
  displayBi: false,
  urlBi: '',
}

const normalizeUser = (user: AdminUser): AdminUser => ({
  id: user.id,
  name: user.name || '',
  email: user.email || '',
  role: user.role || '',
  displayBi: Boolean(user.displayBi),
  urlBi: user.urlBi || '',
})

const EditUser: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [user, setUser] = useState<AdminUser>(emptyUser)
  const [loading, setLoading] = useState(true)
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showBiPreview, setShowBiPreview] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  useEffect(() => {
    const loadUser = async () => {
      try {
        const found = await api.getUserById(Number.parseInt(id as string, 10))
        setUser(normalizeUser(found))
      } catch (error) {
        setAppMessage({
          show: true,
          success: false,
          message: `Erro ao carregar o usuário: ${(
            error as any
          ).messages?.join(', ') ||
            (error as any).message ||
            'tente novamente.'}`,
        })
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [id])

  const updateUser = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    setSaving(true)

    try {
      const updated = await api.updateUser(user.id, {
        name: user.name,
        email: user.email,
        displayBi: user.displayBi,
        urlBi: user.urlBi,
      })
      setUser(normalizeUser(updated))
      setAppMessage({
        show: true,
        success: true,
        message: 'Usuário atualizado com sucesso.',
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: `Erro ao atualizar o usuário: ${(error as any).messages?.join(
          ', '
        ) ||
          (error as any).message ||
          'tente novamente.'}`,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target
    setUser({ ...user, [name]: value })
  }

  return (
    <Page>
      <PageHeader
        back={{ to: '/users', label: 'Usuários' }}
        title="Editar usuário"
      />

      {appMessage.show && (
        <Alert
          variant={appMessage.success ? 'success' : 'danger'}
          onClose={() => setAppMessage({ show: false })}
          dismissible
        >
          {appMessage.message}
        </Alert>
      )}

      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <Form
          noValidate
          validated={showFormErrors}
          onSubmit={updateUser}
          className="card"
          style={{ maxWidth: '48rem' }}
        >
          <div className="card-body">
            <div className="form-grid form-grid-2">
              <Form.Group controlId="formNome">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nome"
                  name="name"
                  value={user.name}
                  onChange={handleFormChange}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Informe o nome do usuário.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={user.email}
                  onChange={handleFormChange}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Informe o email do usuário.
                </Form.Control.Feedback>
              </Form.Group>

              <div className="span-2">
                <CheckRow
                  id="formDisplayBi"
                  checked={user.displayBi}
                  onChange={(displayBi) => setUser({ ...user, displayBi })}
                  title="Exibir Bi Médico"
                />
              </div>

              <Form.Group controlId="formUrlBi" className="span-2">
                <Form.Label>URL Bi Médico</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="https://app.powerbi.com/..."
                  name="urlBi"
                  value={user.urlBi}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
          </div>

          <div className="card-foot">
            {hasBiUrl(user) && (
              <Button
                variant="ghost"
                type="button"
                className="me-auto"
                onClick={() => setShowBiPreview(true)}
              >
                <Icon name="eye" size={16} />
                Preview Bi Médico
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/users')}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Icon name="check" size={16} />
              )}
              Salvar
            </Button>
          </div>
        </Form>
      )}

      <BiPreviewModal
        show={showBiPreview}
        url={user.urlBi}
        onClose={() => setShowBiPreview(false)}
      />
    </Page>
  )
}

export default EditUser
